/**
 * Полоса прогресса шапки квиза — виды `bar`, `nodes`, `segmented` — и решение,
 * рисовать ли полосу и счётчик на этом экране. Вынесено из QuizScreenWizard.tsx
 * без смены разметки (снимки шапки до и после — в
 * __tests__/QuizScreenWizard.hideProgress.test.tsx).
 *
 * Полосу и счётчик гасят (одна дверь `quizHeaderShowsProgress`, её читают
 * все ветки шапки, включая «узлы по разделам»):
 * - блок «Текст прогресса» на экране (WEB-1713) — роль индикатора у блока;
 * - поле экрана `hideProgress` (слово владельца 21.10.2026, Brainway:
 *   информационные экраны с «назад» и логотипом, но без полосы и счётчика).
 * «Назад», «вперёд», «закрыть» и логотип остаются; в раскладке `one-row`
 * на месте полосы остаётся распорка строки (её рисует визард). `hideChrome`
 * главнее: ветка скрытой шапки визарда выходит раньше.
 */
import React from 'react';
import type { EditorScreen } from '@/store/editor/types/screen';
import type { ResolvedQuizHeader } from './quizHeader';

export function quizHeaderShowsProgress(
  screenHasOwnProgressBlock: boolean,
  screen: Pick<EditorScreen, 'hideProgress'> | undefined,
): boolean {
  return !screenHasOwnProgressBlock && screen?.hideProgress !== true;
}

export interface QuizHeaderProgressBarsProps {
  kind: ResolvedQuizHeader['progressKind'];
  /** Ответ `quizHeaderShowsProgress`. */
  show: boolean;
  /** Полоса в строке кнопок (раскладка `one-row`) — без верхнего отступа. */
  oneRow: boolean;
  total: number;
  stepIndex: number;
  ratio: number;
  thickness?: number;
  fillColor?: string;
  fillBackground?: string;
  trackBg: string;
  /**
   * Размер и положение узла вида `nodes` — ответ `progressNodeBox` (своё поле
   * шапки `progressNodeSize`, stylepack/section-steps); нет — узел 10 px, как
   * раньше.
   */
  nodeBox?: { size: number; top: number; half: number };
}

const DEFAULT_NODE_BOX = { size: 10, top: -4, half: 5 };

export function QuizHeaderProgressBars({
  kind,
  show,
  oneRow,
  total,
  stepIndex,
  ratio,
  thickness,
  fillColor,
  fillBackground,
  trackBg,
  nodeBox = DEFAULT_NODE_BOX,
}: QuizHeaderProgressBarsProps) {
  return (
    <>
      {kind === 'segmented' && show && (
        <div style={{ display: 'flex', gap: 4, marginTop: oneRow ? 0 : 8 }}>
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              data-testid={`quiz-progress-segment-${i}`}
              data-state={
                i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'idle'
              }
              style={{
                flex: 1,
                height: thickness ?? 4,
                borderRadius: 2,
                background:
                  i <= stepIndex
                    ? (fillColor ?? 'var(--color-primary, #1677ff)')
                    : trackBg,
                transition: 'background-color var(--transition-ms, 0ms) ease',
              }}
            />
          ))}
        </div>
      )}

      {(kind === 'bar' || kind === 'nodes') && show && (
        <div
          data-testid="quiz-progress-track"
          style={{
            marginTop: oneRow ? 0 : 8,
            height: thickness ?? (kind === 'nodes' ? 2 : 5),
            borderRadius: 999,
            background: trackBg,
            position: 'relative',
          }}
        >
          <span
            data-testid="quiz-progress-fill"
            style={{
              display: 'block',
              width: `${Math.round(ratio * 100)}%`,
              height: '100%',
              borderRadius: 999,
              // Решение владельца №2: градиент дорожки из темы/палитры
              // (--gradient-progress); не задан — прежний сплошной бренд.
              background:
                'var(--gradient-progress, var(--color-primary, #1677ff))',
              // Своё поле цвета или градиента шапки сильнее градиента темы.
              ...(fillBackground ? { background: fillBackground } : {}),
              transition: 'width var(--transition-ms, 0ms) ease',
            }}
          />
          {kind === 'nodes' &&
            Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                data-testid={`quiz-progress-node-${i}`}
                style={{
                  position: 'absolute',
                  top: nodeBox.top,
                  left: `calc(${(i / Math.max(1, total - 1)) * 100}% - ${nodeBox.half}px)`,
                  width: nodeBox.size,
                  height: nodeBox.size,
                  borderRadius: '50%',
                  background:
                    i <= stepIndex
                      ? (fillColor ?? 'var(--color-primary, #1677ff)')
                      : 'var(--color-bg-surface, #fff)',
                  border: `1px solid ${fillColor ?? 'var(--color-primary, #1677ff)'}`,
                }}
              />
            ))}
        </div>
      )}
    </>
  );
}
